import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { EditEntry, Expense } from "../../../entriesAPI";
import {
  MONTHLY_SALARY_EXPENSE_CODE,
  TIME_WORK_EXPENSE_CODE,
} from "../../../constants";
import {
  ArrowDropDownOutlined,
  ArrowDropUpOutlined,
} from "@mui/icons-material";
import { useState } from "react";
import { ExpenseRow } from "./ExpenseRow";
import { calculateTotalExpenses } from "./helpers";
import { useAppSelector } from "../../../../../app/hooks";
import { SalaryType } from "../../../../orders/types/salaryType";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { FlexVerticalAligned } from "../../../../../common/components/styled/styled";

export interface AggregatedExpense extends Omit<Expense, "amount" | "total"> {
  amount: number;
  total: number;
}

interface Props {
  entry: EditEntry;
  setEntry: (val: EditEntry) => void;
  editingActive?: boolean;
}

// TODO: refactor to use EntryCollapse parent component
const PayrollList = ({ entry, setEntry, editingActive }: Props) => {
  const { t } = useTranslation();

  const [payrollIsOpen, setPayrollIsOpen] = useState<boolean>(false);

  // Don't show monthly expenses
  const expenses: Expense[] = Object.values(entry.expenses).filter(
    (expense) => expense.expenseTypeCode !== MONTHLY_SALARY_EXPENSE_CODE,
  );
  const assignment = useAppSelector((state) => state.talents.assignment);
  const allowNotBillable =
    assignment?.employmentSalaryType === SalaryType.Hourly;

  const handleBilled = (checked: boolean) => {
    setEntry({
      ...entry,
      isBillable: !checked,
    });
  };

  const aggregateExpenses: { [key: string]: AggregatedExpense } =
    expenses.reduce(
      (aggregated, expense) => {
        const key = `${expense.expenseTypeCode}-${expense.unitCost}`;
        if (!aggregated[key]) {
          aggregated[key] = {
            ...expense,
            amount: parseFloat(expense.amount),
            total: parseFloat(expense.total),
          };
        } else {
          aggregated[key].amount += parseFloat(expense.amount);
          aggregated[key].total += parseFloat(expense.total);
        }
        return aggregated;
      },
      {} as { [key: string]: AggregatedExpense },
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            color: "#4A1D9C",
            fontSize: "14px",
            fontWeight: "700",
            fontFamily: "quicksand",
            gap: "20px",
            "&:hover": { cursor: "pointer" },
          }}
          onClick={() => setPayrollIsOpen(!payrollIsOpen)}
        >
          {payrollIsOpen ? (
            <>
              {`${t("hourEntries:closePayrollList")}`}
              <ArrowDropUpOutlined color="primary" />
            </>
          ) : (
            <>
              {`${t("hourEntries:openPayrollList")}`}
              <ArrowDropDownOutlined color="primary" />
            </>
          )}
        </Box>

        <FlexVerticalAligned
          sx={{
            display: editingActive ? "none" : "flex",
          }}
        >
          <FormControlLabel
            disabled={!allowNotBillable}
            label={t("hourEntries:notBilled")}
            control={
              <Checkbox
                checked={!entry.isBillable}
                onChange={(e) => handleBilled(e.target.checked)}
              />
            }
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "14px",
                marginRight: "-8px",
              },
            }}
          />
          <InfoTooltip title={t("hourEntries:notBilledTooltip")} />
        </FlexVerticalAligned>
      </Box>

      {payrollIsOpen && (
        <Grid
          container
          sx={{
            display: "flex",
          }}
        >
          <Grid
            item
            xs={4}
            margin={0}
            padding={0}
            sx={{
              paddingRight: "15px",
            }}
          >
            <Typography
              variant="header"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                marginBottom: "16px",
              }}
            >
              {t("hourEntries:basicInfo").toUpperCase()}
            </Typography>
            <Divider
              sx={{
                marginTop: "16px",
                marginBottom: "16px",
              }}
            />
            {Object.keys(aggregateExpenses).map((key, index) => {
              const expense = aggregateExpenses[key];
              if (expense.expenseTypeCode === TIME_WORK_EXPENSE_CODE) {
                return <ExpenseRow key={index} expense={expense} />;
              }
              return null;
            })}
          </Grid>
          <Grid
            item
            xs={4}
            margin={0}
            padding={0}
            sx={{
              paddingRight: "15px",
            }}
          >
            <Typography
              variant="header"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                marginBottom: "16px",
              }}
            >
              {t("hourEntries:salaryComponents").toUpperCase()}
            </Typography>
            <Divider
              sx={{
                marginTop: "16px",
                marginBottom: "16px",
              }}
            />
            {Object.keys(aggregateExpenses).map((key, index) => {
              const expense = aggregateExpenses[key];
              if (expense.expenseTypeCode !== TIME_WORK_EXPENSE_CODE) {
                return <ExpenseRow key={index} expense={expense} />;
              }
              return null;
            })}
          </Grid>
          <Divider
            sx={{
              marginTop: "16px",
              marginBottom: "16px",
            }}
          />
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "center",
              fontSize: "13px",
              fontFamily: "quicksand",
              fontWeight: "500",
            }}
          >
            {`${t("hourEntries:salary").toUpperCase()}: `}
            <Typography
              sx={{
                fontWeight: "700",
                fontSize: "17px",
                marginLeft: "10px",
              }}
            >
              {`${calculateTotalExpenses(expenses)} €`}
            </Typography>
          </Box>
        </Grid>
      )}
    </Box>
  );
};

export default PayrollList;
